import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface NoteExamen {
  id: number;
  etudiant: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  etudiant_classe?: string;
  matiere: number;
  matiere_code: string;
  matiere_nom: string;
  note_examen: string | null;
  absent: boolean;
  est_rattrapage: boolean;
  verrouille: boolean;
  date_limite: string | null;
  est_verrouille: boolean;
  saisi_par: number | null;
  saisi_par_nom: string | null;
  modifie_par: number | null;
  modifie_par_nom: string | null;
  date_saisie: string;
  date_modification: string;
  annee_academique: string;
  semestre: 'S1' | 'S2';
}

export interface NoteExamenCreate {
  etudiant_id: number;
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  note_examen?: number | null;
  absent?: boolean;
  est_rattrapage?: boolean;
}

export interface NoteExamenLigne {
  matricule: string;
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  note_examen?: number | null;
  absent?: boolean;
  est_rattrapage?: boolean;
}

export interface NoteExamenMatricielle {
  matiere_id: number;
  annee_academique: string;
  semestre: 'S1' | 'S2';
  est_rattrapage?: boolean;
  notes: Array<{
    etudiant_id: number;
    note_examen?: number | null;
    absent?: boolean;
  }>;
}

export interface HistoriqueModificationExamen {
  id: number;
  note_examen: number;
  etudiant_matricule: string;
  matiere_nom: string;
  champ_modifie: string;
  ancienne_valeur: string | null;
  nouvelle_valeur: string | null;
  motif: string;
  motif_display: string;
  commentaire: string;
  modifie_par: number | null;
  modifie_par_nom: string | null;
  date_modification: string;
  adresse_ip: string | null;
}

export interface StatsNotesExamens {
  total_notes: number;
  notes_saisies: number;
  notes_manquantes: number;
  moyenne_generale: number;
  notes_verrouillees: number;
  absents: number;
  taux_absence: number;
  notes_eliminatoires: number;
}

export interface ExcelPreview {
  headers: string[];
  preview_rows: any[][];
  mapping_suggere: { [key: string]: number };
  total_rows: number;
}

export interface ImportResult {
  success: boolean;
  total: number;
  crees: number;
  modifies: number;
  erreurs: number;
  details: Array<{
    etudiant_id?: number;
    status: string;
    note?: string;
    error?: string;
  }>;
  erreurs_parsing?: string[];
}

export const notesExamensApi = createApi({
  reducerPath: 'notesExamensApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['NotesExamens', 'HistoriqueModificationsExamens', 'StatsExamens'],
  endpoints: (builder) => ({
    // Lister les notes avec filtres
    getNotesExamens: builder.query<NoteExamen[], {
      annee_academique?: string;
      semestre?: string;
      matiere_id?: number;
      etudiant_id?: number;
      verrouille?: boolean;
      est_rattrapage?: boolean;
      classe?: string;
    }>({
      query: (params) => ({
        url: '/notes-examens/',
        params,
      }),
      // Transformer la réponse paginée en tableau
      transformResponse: (response: any) => {
        // Si c'est un objet paginé, retourner results
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        // Sinon retourner la réponse directement (déjà un tableau)
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['NotesExamens'],
    }),

    // Détail d'une note
    getNoteExamen: builder.query<NoteExamen, number>({
      query: (id) => `/notes-examens/${id}/`,
      providesTags: ['NotesExamens'],
    }),

    // Mode 1: Saisie matricielle
    saisieMatricielle: builder.mutation<ImportResult, NoteExamenMatricielle>({
      query: (data) => ({
        url: '/notes-examens/saisie_matricielle/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    // Mode 2: Saisie individuelle
    saisieIndividuelle: builder.mutation<{
      success: boolean;
      created: boolean;
      note_examen_id: number;
      note: string;
    }, NoteExamenCreate>({
      query: (data) => ({
        url: '/notes-examens/saisie_individuelle/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    // Mode 3: Saisie ligne par ligne
    saisieLigne: builder.mutation<{
      success: boolean;
      created: boolean;
      note_examen_id: number;
      note: string;
    }, NoteExamenLigne>({
      query: (data) => ({
        url: '/notes-examens/saisie_ligne/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    // Prévisualiser Excel
    previewExcel: builder.mutation<ExcelPreview, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/notes-examens/preview_excel/',
          method: 'POST',
          body: formData,
        };
      },
    }),

    // Importer Excel
    importExcel: builder.mutation<ImportResult, {
      file: File;
      mapping: { [key: string]: number };
      matiere_id: number;
      annee_academique: string;
      semestre: 'S1' | 'S2';
      est_rattrapage?: boolean;
    }>({
      query: ({ file, mapping, matiere_id, annee_academique, semestre, est_rattrapage }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mapping', JSON.stringify(mapping));
        formData.append('matiere_id', matiere_id.toString());
        formData.append('annee_academique', annee_academique);
        formData.append('semestre', semestre);
        formData.append('est_rattrapage', String(est_rattrapage || false));
        return {
          url: '/notes-examens/import_excel/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    // Verrouiller les notes
    verrouiller: builder.mutation<{ success: boolean; nb_notes_verrouillees: number }, {
      matiere_id: number;
      annee_academique: string;
      semestre: 'S1' | 'S2';
      est_rattrapage?: boolean;
      date_limite?: string;
    }>({
      query: (data) => ({
        url: '/notes-examens/verrouiller/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesExamens'],
    }),

    // Déverrouiller les notes (Super Admin)
    deverrouiller: builder.mutation<{ success: boolean; nb_notes_deverrouillees: number }, {
      matiere_id: number;
      annee_academique: string;
      semestre: 'S1' | 'S2';
      est_rattrapage?: boolean;
    }>({
      query: (data) => ({
        url: '/notes-examens/deverrouiller/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotesExamens'],
    }),

    // Statistiques
    getStats: builder.query<StatsNotesExamens, {
      annee_academique?: string;
      semestre?: string;
      matiere_id?: number;
      est_rattrapage?: boolean;
    }>({
      query: (params) => ({
        url: '/notes-examens/stats/',
        params,
      }),
      providesTags: ['StatsExamens'],
    }),

    // Historique des modifications
    getHistorique: builder.query<HistoriqueModificationExamen[], {
      note_examen_id?: number;
      modifie_par?: number;
      date_debut?: string;
      date_fin?: string;
    }>({
      query: (params) => ({
        url: '/historique-modifications-notes-examens/',
        params,
      }),
      // Endpoint paginé (PageNumberPagination) → extraire .results
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['HistoriqueModificationsExamens'],
    }),

    // Détail historique
    getHistoriqueDetail: builder.query<HistoriqueModificationExamen, number>({
      query: (id) => `/historique-modifications-notes-examens/${id}/`,
      providesTags: ['HistoriqueModificationsExamens'],
    }),

    // ─── Actions rapides (miroir des devoirs) ─────────────────────────────
    detecterExamensNonFaits: builder.query<DetectionExamensResponse, DetectionExamensParams>({
      query: (params) => ({ url: '/notes-examens/detecter-examens-non-faits/', params }),
      providesTags: ['NotesExamens'],
    }),

    analyserCompletudeExamen: builder.mutation<AnalyseCompletudeExamenResponse, AnalyseCompletudeExamenParams>({
      query: (body) => ({
        url: '/notes-examens/analyser-completude/',
        method: 'POST',
        body,
      }),
    }),

    attribuerZeroAbsents: builder.mutation<BatchExamenResponse, AttribuerZeroAbsentsParams>({
      query: (body) => ({
        url: '/notes-examens/attribuer-zero-absents/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    noteCollectiveExamen: builder.mutation<BatchExamenResponse, NoteCollectiveExamenParams>({
      query: (body) => ({
        url: '/notes-examens/note-collective/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    annulerBatchExamen: builder.mutation<{ success: boolean; batch_id: string; nb_modifications_restaurees: number },
      { batch_id: string; commentaire?: string }>({
      query: (body) => ({
        url: '/notes-examens/annuler-batch/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    // Reporter une note de devoir (D1, D2 ou moyenne) vers la note d'examen
    // Body : { matiere_id, annee_academique, semestre, etudiants_ids,
    //         source: 'note1'|'note2'|'moyenne', mode: 'manquants_seulement'|'tous',
    //         motif?, commentaire? }
    reporterDevoirVersExamenTrace: builder.mutation<
      { success: boolean; batch_id?: string; nb_traites?: number; nb_crees?: number; nb_modifies?: number; nb_ignores?: number; details?: any[] },
      { matiere_id: number; annee_academique: string; semestre: string;
        etudiants_ids: number[];
        source: 'note1' | 'note2' | 'moyenne';
        mode: 'manquants_seulement' | 'tous';
        motif?: string; commentaire?: string }
    >({
      query: (body) => ({
        url: '/notes-examens/reporter-devoir-vers-examen-trace/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    getBatchesRecentsExamens: builder.query<{ batches: BatchRecentExamen[] }, { limit?: number; type_operation?: string }>({
      query: (params) => ({ url: '/notes-examens/batches-recents/', params }),
      providesTags: ['NotesExamens'],
    }),

    // ─── Outils avancés ───────────────────────────────────────────────────
    detecterAnomaliesExamens: builder.query<AnomaliesResponse, AnomaliesParams>({
      query: (params) => ({ url: '/notes-examens/detecter-anomalies/', params }),
      providesTags: ['NotesExamens'],
    }),

    arrondirNotesExamens: builder.mutation<BatchExamenResponse, ArrondirNotesParams>({
      query: (body) => ({
        url: '/notes-examens/arrondir-notes/', method: 'POST', body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    plafondPlancherExamens: builder.mutation<BatchExamenResponse, PlafondPlancherParams>({
      query: (body) => ({
        url: '/notes-examens/plafond-plancher/', method: 'POST', body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),

    // Attribue une note identique aux étudiants ayant une note d'examen <= seuil
    noteSeuilExamen: builder.mutation<NoteSeuilExamenResponse, NoteSeuilExamenParams>({
      query: (body) => ({
        url: '/notes-examens/note-seuil/', method: 'POST', body,
      }),
      invalidatesTags: ['NotesExamens', 'StatsExamens'],
    }),
  }),
});

// ─── Types Outils avancés ──────────────────────────────────────────────────
export interface AnomaliesParams {
  annee_academique: string;
  semestre: string;
  niveau?: string;
  classe?: string;
  matiere_id?: number;
}
export interface AnomalieEntry {
  id: number;
  matricule: string | null;
  nom: string | null;
  classe: string | null;
  matiere: string | null;
  matiere_code: string | null;
  note_examen?: number | null;
  absent?: boolean;
  note1?: number | null;
  note2?: number | null;
  absent1?: boolean;
  absent2?: boolean;
  probleme?: string;
  problemes?: string[];
}
export interface AnomaliesResponse {
  annee_academique: string;
  semestre: string;
  total_anomalies: number;
  hors_plage: AnomalieEntry[];
  incoherentes: AnomalieEntry[];
  incompletes: AnomalieEntry[];
}

export interface ArrondirNotesParams {
  matiere_id: number;
  classes: string[];
  annee_academique: string;
  semestre: string;
  palier: 0.25 | 0.5 | 1;
  sens?: 'proche' | 'sup' | 'inf';
  motif?: string;
  commentaire?: string;
}

export interface PlafondPlancherParams {
  matiere_id: number;
  classes: string[];
  annee_academique: string;
  semestre: string;
  plancher?: number | null;
  plafond?: number | null;
  motif?: string;
  commentaire?: string;
}

// ─── Types Actions rapides examens ──────────────────────────────────────────

export interface DetectionExamensParams {
  annee_academique: string;
  semestre: string;
  niveau?: string;
  pole?: string;
}
export interface ExamenAlertEntry {
  matiere_id: number;
  matiere_nom: string;
  matiere_code: string;
  classe: string;
  niveau: string;
  option: string;
  pole: string;
  nb_etudiants: number;
  nb_avec_note: number;
  nb_absents: number;
  pct_saisie: number;
  statut: 'non_fait' | 'partiel';
  suggestion: string;
}
export interface DetectionExamensResponse {
  annee_academique: string;
  semestre: string;
  total_non_faits: number;
  total_partiels: number;
  examens_non_faits: ExamenAlertEntry[];
  examens_partiels: ExamenAlertEntry[];
}

export interface AnalyseCompletudeExamenParams {
  matiere_id: number;
  annee_academique: string;
  semestre: string;
  classes: string[];
}
export interface EtudiantManquantExamen {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  note_examen_id?: number;
  note_actuelle?: number;
}
export interface AnalyseCompletudeExamenResponse {
  total_etudiants: number;
  nb_avec_note: number;
  nb_absents: number;
  nb_manquants: number;
  pct_avec_note: number;
  pct_absents: number;
  pct_manquants: number;
  manquants: EtudiantManquantExamen[];
  absents: EtudiantManquantExamen[];
}

export interface AttribuerZeroAbsentsParams {
  matiere_id: number;
  etudiants_ids: number[];
  annee_academique: string;
  semestre: string;
  motif?: string;
  commentaire?: string;
}
export interface NoteCollectiveExamenParams {
  matiere_id: number;
  classes: string[];
  annee_academique: string;
  semestre: string;
  valeur: number;
  ecraser_existants?: boolean;
  motif?: string;
  commentaire?: string;
}
export interface NoteSeuilExamenParams {
  matiere_id: number;
  annee_academique: string;
  semestre: string;
  seuil: number;
  valeur: number;
  scope?: 'classes' | 'niveau' | 'parcours' | 'matiere';
  classes?: string[];
  niveau?: string;
  parcours_nom?: string;
  inclure_absents?: boolean;
  motif?: string;
  commentaire?: string;
}
export interface NoteSeuilExamenResponse {
  success: boolean;
  batch_id: string;
  type_operation: string;
  seuil: number;
  valeur: number;
  nb_modifies: number;
  nb_inchangees: number;
  nb_total_traites: number;
  recalcul?: any;
}
export interface BatchExamenResponse {
  success: boolean;
  batch_id: string;
  type_operation: string;
  valeur?: number;
  nb_crees: number;
  nb_modifies: number;
  nb_ignores: number;
  nb_total_traites: number;
  details?: Array<{ matricule: string; action: string }>;
}

export interface BatchRecentExamen {
  batch_id: string;
  type_operation: string;
  date: string;
  nb_modifications: number;
  auteur: string | null;
  matiere_code: string | null;
  matiere_nom: string | null;
  commentaire: string;
  annule: boolean;
}

export const {
  useGetNotesExamensQuery,
  useGetNoteExamenQuery,
  useSaisieMatricielleMutation,
  useSaisieIndividuelleMutation,
  useSaisieLigneMutation,
  usePreviewExcelMutation,
  useImportExcelMutation,
  useVerrouillerMutation,
  useDeverrouillerMutation,
  useGetStatsQuery,
  useGetHistoriqueQuery,
  useGetHistoriqueDetailQuery,
  useDetecterExamensNonFaitsQuery,
  useAnalyserCompletudeExamenMutation,
  useAttribuerZeroAbsentsMutation,
  useNoteCollectiveExamenMutation,
  useAnnulerBatchExamenMutation,
  useReporterDevoirVersExamenTraceMutation,
  useGetBatchesRecentsExamensQuery,
  useDetecterAnomaliesExamensQuery,
  useArrondirNotesExamensMutation,
  usePlafondPlancherExamensMutation,
  useNoteSeuilExamenMutation,
} = notesExamensApi;
